/* eslint-disable no-fallthrough */
import React from 'react'
import { css } from '@emotion/core'
import { Bleed, Ellipsis, Image, Separator, Paragraph, Card, Row, Subheading, colors, usePageWidth } from '@fs/zion-ui'
import { i18n } from '@fs/zion-locale'
import { useTranslation } from 'react-i18next'
import { removeHtml } from '@fs/zion-frontend-friends'
import { MenuNewWindow, ContentLock, ContentUnlock } from '@fs/zion-icon'
import { Link } from '@fs/zion-router'
import { getRelativePath } from '../helpers'
import { placeholderSeafoamJpg } from '../../images'
import {
  AcceptedBadge,
  Badge,
  DeclinedBadge,
  EditableBadge,
  LanguageBadge,
  LiveBadge,
  SeriesBadge,
  SubmittedBadge,
} from '../Badge'
import Timestamp from '../Timestamp'
import RainbowDivider from '../RainbowDivider'
import WatchProgressBar from '../WatchProgressBar'
import { StrongLink, PossibleLink } from '../link'
import PlaylistsButton from '../buttons/PlaylistsButton'
import FollowSeriesButton from '../buttons/FollowSeriesButton'
import { navConfig, sessionStates } from '../../config'
import { MonoText } from '../type'
import zionHeadingMap from './zionHeadingMap'

const languageBadgeCss = css`
  position: absolute;
  bottom: 16px;
  inset-inline-start: 16px;
`

const durationBadgeCss = css`
  position: absolute;
  bottom: 16px;
  inset-inline-end: 16px;
`

const cardImageCss = css`
  position: relative;

  & a:focus {
    img {
      opacity: 75%;
      border-radius: 8px;
      border: 2px solid white;
    }
  }
`
const getLinkName = (item, scrubbedHeading, type) => {
  if (item?.exhibitorName) {
    return `${item.exhibitorName.slice(0, 10)}: Promo: ${scrubbedHeading}`
  }
  return `Card: ${scrubbedHeading}: ${type}`
}

export const ImageBadges = ({ contentLocale, duration }) => {
  const durationItems = duration?.split(':').reverse()
  const hours = durationItems?.[2] ?? 0
  const minutes = durationItems?.[1] ?? 0
  const seconds = durationItems?.[0] ?? 0
  const durationIsZero = Number(duration?.replace(':', '')) === 0
  const prettyDuration = duration?.substring(duration.match('[1-9]')?.index ?? 0)
  const formattedDuration = (minutes === '0' ? '00:' : '') + prettyDuration

  return (
    <>
      <div css={languageBadgeCss}>
        <LanguageBadge contentLocale={contentLocale} />
      </div>
      {duration && !durationIsZero && (
        <Badge
          flavor="charcoal"
          label={<time dateTime={`PT${hours}H${minutes}M${seconds}S`}>{formattedDuration}</time>}
          css={durationBadgeCss}
        />
      )}
    </>
  )
}

export const HeaderBadges = ({ isSeries, isLive, workflowState }) => {
  const hasBadge = isSeries || isLive || (workflowState && workflowState)
  return hasBadge ? (
    <>
      <Row gutters="nano" wrap>
        {isSeries && <SeriesBadge />}
        {isLive && <LiveBadge />}
        {workflowState === sessionStates.SUBMITTED && <SubmittedBadge />}
        {workflowState === sessionStates.PUBLISHED && <SubmittedBadge />}
        {workflowState === sessionStates.ACCEPTED && <AcceptedBadge />}
        {workflowState === sessionStates.DECLINED && <DeclinedBadge />}
        {[sessionStates.EDITABLE, sessionStates.DRAFT].includes(workflowState) && <EditableBadge />}
      </Row>
      <Separator size="xxs" />
    </>
  ) : null
}

export const CardFooterSection = ({
  actions,
  isPlaylistable,
  contentId,
  external,
  heading,
  isSeries,
  linkProps,
  lockedState,
  item,
}) => {
  const atWidth = usePageWidth()
  const { useIconButton, buttonSize } = atWidth({
    default: { useIconButton: true },
    xl: { useIconButton: false, buttonSize: 'sm' },
  })
  return (
    <Row alignY="middle">
      <div growX>{actions}</div>
      {lockedState === 'lock' && <ContentLock background="black" color="white" size="sm" />}
      {lockedState === 'unlock' && <ContentUnlock background="black" color="white" size="sm" />}
      {isPlaylistable && (
        <>
          {!isSeries && (
            <PlaylistsButton item={item} sessionId={contentId} size={buttonSize} asIconButton={useIconButton} />
          )}
          {isSeries && <FollowSeriesButton seriesId={contentId} size={buttonSize} asIconButton={useIconButton} />}
        </>
      )}
      {external && (
        <MonoText>
          <Link {...linkProps} linkName={getLinkName(item, heading, 'External')} aria-label={heading}>
            <MenuNewWindow />
          </Link>
        </MonoText>
      )}
    </Row>
  )
}

export default function NewCard({
  image = placeholderSeafoamJpg,
  maxImageHeight,
  noImage = false,
  heading,
  subHeading,
  children,
  description,
  descriptionLineClamp = 3,
  // Badges/Buttons
  contentLocale,
  conferenceYear,
  duration,
  startTimestamp,
  isLive,
  lockedState,
  workflowState,
  // ----
  contentId,
  isPlaylistable,
  isInPlaylist,
  dataTestId,
  actions,
  to,
  external,
  flavor,
  noCrop = false,
  percentageWatched = 0,
  force16By9 = false,
  item,
  as = 'h3',
  ...props
}) {
  const [t] = useTranslation()

  const scrubText = (text) => {
    return removeHtml(text)
      ?.replace(/&nbsp;/g, ' ')
      .replace(/&amp;/g, '&')
  }
  const url = external ? to : getRelativePath(to)
  const sessionUrls = ['/session/', '/special-session/', '/registered-session/']
  const isSeries = url?.includes('/series/')
  const isSession = sessionUrls.some((sessionUrl) => url?.includes(sessionUrl))
  const videoUrls = ['/series/', ...sessionUrls]
  const isVideo = force16By9 || videoUrls.some((videoUrl) => url?.includes(videoUrl))

  // HACK: we failed to add an exeption for links that go to *.familysearch.org that are not hosted by familysearch.
  const shouldPrependLangCode = navConfig.langCodeBlackList.some((langCode) => to?.includes(langCode))

  // This is a little trick to only send the prop if it is defined. Not sure why it still shows in the DOM when undefined, but I guess this'll work...
  const noPrependLocaleCode = shouldPrependLangCode ? { noPrependLocaleCode: true } : {}

  const linkProps = {
    to: url,
    external,
    target: external ? '_blank' : '_self',
    ...noPrependLocaleCode,
  }
  const scrubbedHeading = scrubText(heading)
  const Tag = zionHeadingMap[as]
  const userLocale = i18n.language
  const locale = contentLocale?.split('-')[0]
  const contentIsInUsersLanguage = locale === userLocale

  return (
    <>
      {heading && (
        <Card data-testid={dataTestId || 'card-all'} {...props}>
          {!noImage && (
            <>
              <Bleed top sides>
                <div css={cardImageCss}>
                  <PossibleLink linkName={getLinkName(item, scrubbedHeading, 'Image')} {...linkProps}>
                    <Image
                      src={image}
                      style={{ backgroundColor: 'white', aspectRatio: isVideo ? '16/9' : '3/2' }}
                      noCrop={noCrop}
                      alt={t('card.image.alt.prefix', 'Thumbnail for {heading}', { heading: scrubbedHeading })}
                      maxHeight={maxImageHeight}
                      loading="lazy"
                    />
                  </PossibleLink>
                  <ImageBadges contentLocale={contentLocale} duration={duration} />
                  {isSession && (
                    <div style={{ position: 'absolute', bottom: 0, width: '100%' }}>
                      <WatchProgressBar label={scrubbedHeading} percentageWatched={percentageWatched} />
                    </div>
                  )}
                </div>
              </Bleed>
              <Separator size="nano" />
            </>
          )}
          <div growY {...(!contentIsInUsersLanguage && { lang: locale })}>
            {(conferenceYear || startTimestamp || isLive) && (
              <>
                <Row gutters="lg">
                  <div growX />
                  <Timestamp
                    alignX="end"
                    timestamp={startTimestamp}
                    conferenceYear={conferenceYear}
                    isActive={isLive}
                  />
                </Row>
                {/* This is equal to a pico separator */}
                <div style={{ height: 4 }} />
              </>
            )}
            <HeaderBadges isSeries={isSeries} isLive={isLive} workflowState={workflowState} />
            <Tag size="H6" data-testid="card-header">
              <MonoText>
                <Ellipsis
                  as={StrongLink}
                  noUnderline
                  linkName={getLinkName(item, scrubbedHeading, 'Title')}
                  {...linkProps}
                  lineClamp={descriptionLineClamp}
                >
                  {scrubbedHeading}
                </Ellipsis>
              </MonoText>
              <Subheading>
                {subHeading ? (
                  <Ellipsis lineClamp={descriptionLineClamp} noTooltip>
                    {scrubText(subHeading)}
                  </Ellipsis>
                ) : undefined}
              </Subheading>
            </Tag>
            {flavor && <RainbowDivider thick and full of flavor={flavor} />}
            {children}
            {description && (
              <>
                <Separator size="nano" />
                <Paragraph size={noImage ? 'md' : 'sm'}>
                  <span style={{ color: colors.gray80 }}>
                    <Ellipsis lineClamp={isSeries ? 1 : descriptionLineClamp}>{scrubText(description)}</Ellipsis>
                  </span>
                </Paragraph>
              </>
            )}
          </div>
          <Separator size="xxs" />
          <CardFooterSection
            actions={actions}
            startTimestamp={startTimestamp}
            conferenceYear={conferenceYear}
            isPlaylistable={isPlaylistable}
            isInPlaylist={isInPlaylist}
            contentId={contentId}
            dataTestId={dataTestId}
            external={external}
            heading={heading}
            isLive={isLive}
            isSeries={isSeries}
            linkProps={linkProps}
            lockedState={lockedState}
            item={item}
          />
        </Card>
      )}
    </>
  )
}
